import type { CSSProperties, ReactNode } from 'react';

/**
 * Button — the one control, ported from views/components/button.ejs.
 *
 * The class names are the contract with public/app.css: `.btn` owns height, padding, radius and the
 * variant skins. A caller passing padding/height here would be re-creating the 36 hand-tuned buttons the
 * component was written to delete, so `style` is typed to layout only and the JSDoc says so.
 */
export type ButtonVariant = 'primary' | 'ghost' | 'secondary' | 'quiet' | 'danger';
export type ButtonSize = 'md' | 'sm' | 'xs';
export type ButtonShape = 'text' | 'icon' | 'menu-row' | 'inline';

export type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  block?: boolean;
  /** Extra classes the caller owns (arrow-btn, js-copy, …). */
  className?: string;
  /** Layout the component has no size for: margin, display:none. Never padding/height/width. */
  style?: CSSProperties;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  /** An inline <svg> placed before the caption — menu rows and icon buttons need one. */
  icon?: ReactNode;
  /** aria-* / data-* / title / id. */
  title?: string;
  /** The EJS button component takes an `attrs` map; ids are how the pages and the browser suite find a button. */
  id?: string;
  ariaLabel?: string;
  /** Extra attributes, the EJS component's `attrs` map: aria-expanded on a trigger whose menu state a page owns. */
  attrs?: Record<string, string | boolean | undefined>;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Keyboard, for a trigger whose menu opens on ArrowDown (useMenu's contract). */
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  /** React 19 passes ref as a prop: a menu trigger needs the element for placement and focus return. */
  ref?: React.Ref<HTMLButtonElement>;
  children?: ReactNode;
};

const VARIANT: Record<ButtonVariant, string> = {
  primary: '',
  ghost: ' ghost',
  secondary: ' secondary',
  quiet: ' btn-quiet',
  danger: ' btn-danger',
};

// An explicit map rather than a template string: `btn-${size}` emits the same class but is invisible to
// a grep, and test/spa-components.test.js is what proves both front ends share one stylesheet.
const SIZE: Record<ButtonSize, string> = { md: '', sm: ' btn-sm', xs: ' btn-xs' };

const SHAPE: Partial<Record<ButtonShape, string>> = {
  icon: ' btn-icon',
  'menu-row': ' pane-menu-item',
  inline: ' btn-inline',
};

export function Button({
  variant = 'primary',
  size = 'md',
  shape = 'text',
  block,
  className,
  attrs,
  style,
  type = 'button',
  disabled,
  icon,
  title,
  id,
  ariaLabel,
  onClick,
  onKeyDown,
  ref,
  children,
}: ButtonProps) {
  const cls =
    'btn' +
    VARIANT[variant] +
    SIZE[size] +
    (SHAPE[shape] ?? '') +
    (block ? ' btn-block' : '') +
    (className ? ` ${className}` : '');
  return (
    <button
      ref={ref}
      type={type}
      id={id}
      className={cls}
      {...(attrs || {})}
      style={style}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      {icon}
      {children}
    </button>
  );
}
